import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from 'react'
import { User, Session } from '@supabase/supabase-js'
import { useNavigate, useLocation } from 'react-router-dom'
import { forceLocalSignOut, supabase } from '@/lib/supabase'
import { toast } from '@/hooks/use-toast'
import { getUserRole, type UserRole } from '@/lib/account/account-roles'
import { syncTourPreferencesFromDatabase } from '@/lib/tour-utils'
import { isAuthDebugEnabled } from '@/lib/debug'
import { canAccessCoaching, isFreeTrialActive } from '@/lib/account/account-entitlements'
import {
  resolvePostAuthDestination,
  snapshotGuestLocalStorage,
  snapshotGuestSessionStorage,
  restoreGuestStorageSnapshots,
} from '@/message-analyzer/lib/guest-coaching-resume'
import { isPublicAppPath } from '@/lib/routing/public-paths'

const MANUAL_SIGNOUT_GUARD_KEY = 'lina_manual_signout_guard'

function setManualSignoutGuard(): void {
  try {
    sessionStorage.setItem(MANUAL_SIGNOUT_GUARD_KEY, '1')
  } catch {
    // ignore storage failures
  }
}

function clearManualSignoutGuard(): void {
  try {
    sessionStorage.removeItem(MANUAL_SIGNOUT_GUARD_KEY)
  } catch {
    // ignore storage failures
  }
}

function hasManualSignoutGuard(): boolean {
  try {
    return sessionStorage.getItem(MANUAL_SIGNOUT_GUARD_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * OAuth and some flows omit user_metadata.signup_source. Tag those users as web ("app") so
 * accounts.signup_source stays accurate (DB trigger syncs from raw_user_meta_data).
 * Extension users set from_extension / source or are patched on /extension-success.
 */
function webUserNeedsSignupSourceApp(user: User): boolean {
  const m = (user.user_metadata ?? {}) as Record<string, unknown>
  const src = typeof m.signup_source === 'string' ? m.signup_source.trim().toLowerCase() : ''
  if (src === 'app' || src === 'extension' || src === 'unknown') return false
  if (m.from_extension === true || m.from_ce === true) return false
  const source = typeof m.source === 'string' ? m.source.trim().toLowerCase() : ''
  if (source === 'extension' || source === 'reddit') return false
  const platform = typeof m.platform === 'string' ? m.platform.trim().toLowerCase() : ''
  if (platform === 'extension' || platform === 'reddit') return false
  return true
}

async function ensureWebSignupSourceAppMetadata(user: User): Promise<void> {
  if (typeof window !== 'undefined') {
    const path = window.location.pathname || ''
    if (path.includes('/extension-success')) return
    const qs = new URLSearchParams(window.location.search || '')
    if (
      qs.get('from_extension') === 'true' ||
      qs.get('from_ce') === 'true' ||
      qs.get('source') === 'extension'
    ) {
      return
    }
  }
  if (!webUserNeedsSignupSourceApp(user)) return
  const { error } = await supabase.auth.updateUser({
    data: {
      ...user.user_metadata,
      signup_source: 'app',
    },
  })
  if (error && isAuthDebugEnabled()) {
    console.warn('[Auth] ensureWebSignupSourceAppMetadata:', error.message)
  }
}

// Account data interface
export interface AccountData {
  id: string
  user_id: string
  first_name: string | null
  birthdate: string | null
  onboarding_completed: boolean
  is_admin: boolean | null
  is_scoring_admin: boolean | null
  plan: string | null
  free_trial_ends_at: string | null
  organization_id: string | null
  household_id: string | null
  account_type: string | null
  created_at: string
  updated_at: string
  // Preferences (from user_preferences table - joined)
  language_override: string | null
  theme: string | null
  hide_analyzer_help: boolean | null
  // Computed role from membership tables (canonical source)
  role?: UserRole
}

// In-memory only: do not persist is_admin/user_id to localStorage/sessionStorage
const ACCOUNT_CACHE_KEY = 'lina-account-cache'
const accountCacheMemory = new Map<string, string>()

// Cacheable fields that rarely change
interface CachedAccountData {
  is_admin: boolean | null
  user_id: string // Used to validate cache
  role?: UserRole // Computed from membership tables
}

interface AuthContextType {
  user: User | null
  session: Session | null
  accountId: string | null
  accountData: AccountData | null
  /** Supabase anonymous JWT / guest user (before identity link). */
  isAnonymous: boolean
  /** Credentialed user (not anonymous). */
  isKnown: boolean
  isPaid: boolean
  isInFreeTrial: boolean
  canAccessExploreCoaching: boolean
  isLoading: boolean
  isAccountLoading: boolean
  refreshAccountData: (options?: { force?: boolean }) => Promise<void>
  updateAccountField: (field: keyof AccountData, value: any) => void
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  accountId: null,
  accountData: null,
  isAnonymous: false,
  isKnown: false,
  isPaid: false,
  isInFreeTrial: false,
  canAccessExploreCoaching: false,
  isLoading: true,
  isAccountLoading: true,
  refreshAccountData: async () => {},
  updateAccountField: () => {},
})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [accountId, setAccountId] = useState<string | null>(null)
  const [accountData, setAccountData] = useState<AccountData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAccountLoading, setIsAccountLoading] = useState(true)
  const missingAccountSignOutRef = useRef(false)
  const fetchInProgressRef = useRef<string | null>(null) // Track ongoing fetches
  const initSessionStartedRef = useRef(false) // Ensure init runs only once per mount (avoids Strict Mode double-call flood)
  const navigate = useNavigate()
  const location = useLocation()

  const MAX_ACCOUNT_RETRIES = 5
  const ACCOUNT_RETRY_DELAY_MS = 1000

  // One-time: remove any old account cache from storage (we now use in-memory only)
  useEffect(() => {
    try {
      localStorage.removeItem(ACCOUNT_CACHE_KEY)
      sessionStorage.removeItem(ACCOUNT_CACHE_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  // Cache helpers
  // Note: getCachedAccountData currently unused but kept for Phase 7 optimizations

  const getCachedAccountData = (userId: string): CachedAccountData | null => {
    try {
      const cached = accountCacheMemory.get(ACCOUNT_CACHE_KEY) ?? null
      if (!cached) return null

      const data: CachedAccountData = JSON.parse(cached)
      if (data.user_id !== userId) {
        accountCacheMemory.delete(ACCOUNT_CACHE_KEY)
        return null
      }
      if (isAuthDebugEnabled())
        console.log('[AuthContext] Using cached account data:', data.role || 'individual')
      return data
    } catch (e) {
      console.error('[AuthContext] Failed to read cache:', e)
      accountCacheMemory.delete(ACCOUNT_CACHE_KEY)
      return null
    }
  }

  const setCachedAccountData = (data: CachedAccountData) => {
    try {
      accountCacheMemory.set(ACCOUNT_CACHE_KEY, JSON.stringify(data))
    } catch (e) {
      console.error('[AuthContext] Failed to write cache:', e)
    }
  }

  const clearCachedAccountData = () => {
    accountCacheMemory.delete(ACCOUNT_CACHE_KEY)
  }

  const resetMissingAccountGuard = () => {
    missingAccountSignOutRef.current = false
  }

  const signOutForMissingAccount = useCallback(
    async (userId?: string) => {
      if (missingAccountSignOutRef.current) {
        return
      }
      missingAccountSignOutRef.current = true

      console.error(
        `[Auth] Account record not found after retries. Enforcing logout for user_id: ${userId ?? 'unknown'}`,
      )

      toast({
        title: "We couldn't load your account",
        description: 'Please sign in again. If the issue persists, contact support so we can help.',
        variant: 'destructive',
      })

      setAccountData(null)
      setAccountId(null)
      setUser(null)
      setSession(null)
      setIsAccountLoading(false)
      setIsLoading(false)

      try {
        const preservedLocal = snapshotGuestLocalStorage()
        const preservedSession = snapshotGuestSessionStorage()
        localStorage.clear()
        sessionStorage.clear()
        restoreGuestStorageSnapshots(preservedLocal, preservedSession)
        await forceLocalSignOut()
      } catch (error) {
        console.error('[Auth] Error during forced sign out:', error)
      } finally {
        setTimeout(() => {
          navigate('/auth/login', { replace: true })
        }, 0)
      }
    },
    [navigate],
  ) // navigate from useNavigate should be stable

  // Function to fetch account data from database
  const fetchAccountData = useCallback(
    async (userId: string) => {
      // Prevent duplicate fetches for the same user
      if (fetchInProgressRef.current === userId) {
        if (isAuthDebugEnabled())
          console.log(
            '[AuthContext] Fetch already in progress for userId:',
            userId,
            '- skipping duplicate',
          )
        return
      }

      if (isAuthDebugEnabled()) {
        console.log('[AuthContext] fetchAccountData called for userId:', userId)
      }
      fetchInProgressRef.current = userId

      try {
        setIsAccountLoading(true)

        // Unified identity: accounts.id = auth.users.id
        setAccountId(userId)

        let result = await supabase
          .from('accounts')
          .select(
            `
            id,
            user_id,
            first_name,
            birthdate,
            onboarding_completed,
            is_admin,
            is_scoring_admin,
            plan,
            free_trial_ends_at,
            organization_id,
            household_id,
            account_type,
            hide_analyzer_help,
            created_at,
            updated_at
          `,
          )
          .eq('id', userId) // Query by id directly
          .maybeSingle()

        // Same as legacy mode: handle_new_user may commit slightly after the JWT is available.
        if (!result.data && (!result.error || result.error.code === 'PGRST116')) {
          let retryCount = 0
          while (
            retryCount < MAX_ACCOUNT_RETRIES &&
            !result.data &&
            (!result.error || result.error.code === 'PGRST116')
          ) {
            await new Promise((resolve) => setTimeout(resolve, ACCOUNT_RETRY_DELAY_MS))
            result = await supabase
              .from('accounts')
              .select(
                `
            id,
            user_id,
            first_name,
            birthdate,
            onboarding_completed,
            is_admin,
            is_scoring_admin,
            plan,
            free_trial_ends_at,
            organization_id,
            household_id,
            account_type,
            hide_analyzer_help,
            created_at,
            updated_at
          `,
              )
              .eq('id', userId)
              .maybeSingle()
            retryCount++
          }
        }

        if (isAuthDebugEnabled())
          console.log('[AuthContext] Unified mode query result:', {
            data: result.data,
            error: result.error,
          })

        const data = result.data
        const error = result.error

        // Fetch user_preferences
        let userPrefs = null
        if (data) {
          const { data: prefsData } = await supabase
            .from('user_preferences')
            .select('language_override, theme')
            .eq('user_id', userId)
            .maybeSingle()
          userPrefs = prefsData
        }

        if (data) {
          // Compute role from membership tables (canonical source)
          const role = await getUserRole(data.id)

          const accountData: AccountData = {
            id: data.id,
            user_id: data.user_id,
            first_name: data.first_name,
            birthdate: data.birthdate,
            onboarding_completed: data.onboarding_completed,
            is_admin: data.is_admin,
            is_scoring_admin: data.is_scoring_admin,
            plan: data.plan ?? 'free',
            free_trial_ends_at: data.free_trial_ends_at ?? null,
            organization_id: data.organization_id,
            household_id: data.household_id,
            account_type: data.account_type ?? null,
            hide_analyzer_help: data.hide_analyzer_help,
            created_at: data.created_at,
            updated_at: data.updated_at,
            // From user_preferences (reading_level and response_tone are in user_preferences, not accounts)
            language_override: userPrefs?.language_override || null,
            theme: userPrefs?.theme || null,
            // Computed role from membership tables
            role: role || undefined,
          }
          setAccountData(accountData)

          syncTourPreferencesFromDatabase().catch((err) => {
            console.error('[AuthContext] Failed to sync tour preferences:', err)
          })

          setCachedAccountData({
            is_admin: data.is_admin,
            user_id: userId,
            role: role || undefined,
          })
        } else {
          setAccountData(null)
          if (!error || error.code === 'PGRST116') {
            await signOutForMissingAccount(userId)
          }
        }
      } catch (error) {
        console.error('[AuthContext] Error in fetchAccountData:', {
          error,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          userId,
        })
        setAccountData(null)
        await signOutForMissingAccount(userId)
      } finally {
        setIsAccountLoading(false)
        fetchInProgressRef.current = null // Clear the lock
      }
    },
    [signOutForMissingAccount],
  )

  // Refresh function that can be called from components
  const updateAccountField = useCallback((field: keyof AccountData, value: any) => {
    setAccountData((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        [field]: value,
      }
    })
  }, [])

  const refreshAccountData = useCallback(
    async (options?: { force?: boolean }) => {
      if (accountId && (options?.force || !isAccountLoading)) {
        await fetchAccountData(accountId)
      }
    },
    [accountId, isAccountLoading, fetchAccountData],
  ) // Include fetchAccountData in deps

  // Flag to prevent auto-login loops
  const hasAttemptedAutoLogin = useRef(false)

  // Allow unauthenticated access to public routes.
  // Explore page auto-creates anonymous sessions on submit.
  // Lens B2B audit reports authenticate via ?token=, not Supabase session.
  const isPublicPathNow = (pathname: string) => isPublicAppPath(pathname)

  /** Recover session from URL hash (magic link / OAuth redirect). Returns true if session was set. */
  const tryRecoverSessionFromHash = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined' || !window.location.hash) return false
    const hash = window.location.hash.replace(/^#/, '')
    const params = new URLSearchParams(hash)
    const access_token = params.get('access_token')
    const refresh_token = params.get('refresh_token')
    if (!access_token) return false
    try {
      const { error } = await supabase.auth.setSession({
        access_token,
        refresh_token: refresh_token ?? undefined,
      })
      if (error) {
        if (isAuthDebugEnabled())
          console.log('[AuthContext] setSession from hash failed:', error.message)
        return false
      }
      if (isAuthDebugEnabled()) console.log('[AuthContext] Recovered session from URL hash')
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      return true
    } catch (e) {
      if (isAuthDebugEnabled()) console.log('[AuthContext] setSession from hash error:', e)
      return false
    }
  }, [])

  useEffect(() => {
    let isNavigating = false

    // Run init at most once per mount (avoids duplicate session reads from Strict Mode or re-runs)
    if (initSessionStartedRef.current) return
    initSessionStartedRef.current = true

    // Capture initial location for one-time checks
    const initialPathname = location.pathname

    supabase.auth
      .getSession()
      .then(async ({ data: { session: initialSession }, error }) => {
        // Handle invalid refresh token errors
        if (error) {
          console.error('[AuthContext] Session error:', error)

          if (
            error.message?.includes('Invalid Refresh Token') ||
            error.message?.includes('Refresh Token Not Found')
          ) {
            if (isAuthDebugEnabled())
              console.log('[AuthContext] Invalid refresh token detected, signing out...')
            forceLocalSignOut().catch(console.error)
            setSession(null)
            setUser(null)
            setAccountData(null)
            setAccountId(null)
            setIsLoading(false)
            setIsAccountLoading(false)

            // Redirect to try/login (allow /demo for unauthenticated users)
            const pathname =
              typeof window !== 'undefined' ? window.location.pathname : initialPathname
            if (!isPublicPathNow(initialPathname) && !isPublicPathNow(pathname)) {
              isNavigating = true
              setTimeout(() => {
                if (!isNavigating) return
                if (typeof window !== 'undefined' && isPublicPathNow(window.location.pathname))
                  return
                navigate('/auth/login', {
                  replace: true,
                  state: { from: location },
                })
              }, 0)
            }
            return
          }
        }

        // If no session but URL has magic-link tokens in the hash, recover before redirecting.
        // This avoids redirecting to login/try before hash recovery runs.
        let session = initialSession
        if (
          !session &&
          typeof window !== 'undefined' &&
          window.location.hash?.includes('access_token')
        ) {
          const recovered = await tryRecoverSessionFromHash()
          if (recovered) {
            const {
              data: { session: s },
            } = await supabase.auth.getSession()
            session = s
          }
        }

        setSession(session)
        setUser(session?.user ?? null)
        setIsLoading(false)
        resetMissingAccountGuard()

        // Fetch account data if user is logged in
        if (session?.user?.id) {
          fetchAccountData(session.user.id)
        } else {
          setIsAccountLoading(false)
          setAccountId(null) // Only set null if no session
        }

        // Handle no-session on protected routes.
        // Only run redirect when we did NOT just recover from hash (avoids redirect races).
        const currentPath =
          typeof window !== 'undefined' ? window.location.pathname : initialPathname
        const isPublic = isPublicPathNow(initialPathname) || isPublicPathNow(currentPath)
        if (!session && !isPublic && !hasAttemptedAutoLogin.current) {
          if (hasManualSignoutGuard()) {
            if (isAuthDebugEnabled()) {
              console.log(
                '[AuthContext] Manual sign-out guard active, skipping auto-login recovery.',
              )
            }
            clearManualSignoutGuard()
            if (!isNavigating) {
              isNavigating = true
              setTimeout(() => {
                if (!isNavigating) return
                if (typeof window !== 'undefined' && isPublicPathNow(window.location.pathname))
                  return
                navigate('/auth/login', {
                  replace: true,
                  state: { from: location },
                })
              }, 0)
            }
            return
          }
          hasAttemptedAutoLogin.current = true
          // Canonical auth model: guest uses anonymous Supabase session; known users sign in with credentials.
          // If no session exists here, redirect to login.
          if (!isNavigating) {
            isNavigating = true
            setTimeout(() => {
              if (!isNavigating) return
              if (typeof window !== 'undefined' && isPublicPathNow(window.location.pathname)) return
              navigate('/auth/login', {
                replace: true,
                state: { from: location },
              })
            }, 0)
          }
        }
      })
      .catch((error) => {
        // Ignore AbortError - it's expected during cleanup
        if (error?.name === 'AbortError') {
          // Still set loading to false so app isn't stuck
          setIsLoading(false)
          setIsAccountLoading(false)
          return
        }

        console.error('[AuthContext] Failed to get session:', error)
        if (
          error.message?.includes('Invalid Refresh Token') ||
          error.message?.includes('Refresh Token Not Found')
        ) {
          forceLocalSignOut().catch(console.error)
        }
        setIsLoading(false)
        setIsAccountLoading(false)
      })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Handle token refresh errors globally
      if (event === 'TOKEN_REFRESHED' && !session) {
        if (isAuthDebugEnabled()) console.log('[Auth] Token refresh failed - signing out user')
        await forceLocalSignOut()
        return
      }

      setSession(session)
      setUser(session?.user ?? null)
      setIsLoading(false)
      if (session?.user?.id) {
        resetMissingAccountGuard()
        clearManualSignoutGuard()
      }

      if (
        session?.user &&
        (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'USER_UPDATED')
      ) {
        await ensureWebSignupSourceAppMetadata(session.user)
      }

      // Fetch account data on sign in
      if (session?.user?.id) {
        fetchAccountData(session.user.id)
      } else {
        setAccountData(null)
        setIsAccountLoading(false)
      }

      // Handle sign out - redirect to login (except for invite pages)
      if (event === 'SIGNED_OUT') {
        // Check if we're in the middle of an email change - don't redirect
        const isChangingEmail = sessionStorage.getItem('lina_changing_email') === 'true'
        if (isChangingEmail) {
          if (isAuthDebugEnabled())
            console.log('[Auth] SIGNED_OUT during email change - staying on page')
          return
        }

        setAccountData(null)
        setAccountId(null) // Clear accountId on signout
        clearCachedAccountData() // Clear cached account metadata
        setManualSignoutGuard()

        // Don't redirect if on invite page or public /demo - user needs to stay there
        // Use window.location to get current path (not stale closure)
        const currentPath = window.location.pathname
        if (
          currentPath.includes('/auth/invite') ||
          currentPath.includes('/auth/change-email') ||
          currentPath.startsWith('/demo')
        ) {
          if (isAuthDebugEnabled())
            console.log('[Auth] SIGNED_OUT on special auth page or /demo - staying on page')
          return
        }

        const preservedLocal = snapshotGuestLocalStorage()
        const preservedSession = snapshotGuestSessionStorage()
        localStorage.clear()
        sessionStorage.clear()
        restoreGuestStorageSnapshots(preservedLocal, preservedSession)

        isNavigating = true
        setTimeout(() => {
          // Redirect to login page after logout
          navigate('/auth/login', { replace: true, state: null })
        }, 0)
      }

      // Handle sign in - always redirect to root to ensure proper account-type routing
      if (event === 'SIGNED_IN' && session) {
        // Use window.location to get current path (not stale closure)
        const currentPath = window.location.pathname
        const query = new URLSearchParams(window.location.search)
        const isExtensionAuthPage =
          currentPath === '/auth/extension/login' || currentPath === '/auth/extension/signup'
        const isFromExtensionFlow =
          query.get('from_extension') === 'true' ||
          query.get('from_ce') === 'true' ||
          query.get('source') === 'extension'
        const isAuthSuccessPath =
          currentPath === '/auth/success' || currentPath === '/extension-success'
        // Keep users on password recovery page while they set a new password.
        const isPasswordRecoveryPath =
          currentPath === '/auth/update-password' ||
          currentPath === '/auth/updatepasswordpage' ||
          currentPath === '/auth/auth/update-password' ||
          currentPath === '/auth/auth/updatepasswordpage'
        // Only redirect if on auth pages - preserve current location in app
        const isOnAuthPage = currentPath.startsWith('/auth')

        // Any extension-sourced auth flow must hand off through /extension-success
        // so the extension relay can close/update panel state, even if it used
        // a shared auth route like /auth/login.
        if (isFromExtensionFlow && !isAuthSuccessPath) {
          const successParams = new URLSearchParams({
            from_extension: 'true',
            from_ce: 'true',
            source: 'extension',
            from_iframe: query.get('from_iframe') === 'true' ? 'true' : 'false',
          })
          isNavigating = true
          setTimeout(() => {
            window.location.assign(`/extension-success?${successParams.toString()}`)
          }, 0)
          return
        }

        // Only redirect from auth pages after sign-in, not when already in the app.
        if (isOnAuthPage && !isPasswordRecoveryPath && !isAuthSuccessPath) {
          isNavigating = true
          setTimeout(() => {
            window.location.assign(resolvePostAuthDestination('/'))
          }, 0)
        }
        // If already on an app page, stay there (don't navigate)
      }
    })

    return () => {
      isNavigating = false
      subscription.unsubscribe()
    }
    // Remove location from deps to prevent re-subscription on every navigation
  }, [navigate])

  const isAnonymous = user?.is_anonymous === true
  const isKnown = !!user && !isAnonymous
  const isPaid = accountData?.plan === 'paid' || accountData?.is_admin === true
  const isInFreeTrial = isFreeTrialActive(accountData?.free_trial_ends_at)
  const exploreCoaching = canAccessCoaching({
    isAnonymous,
    isPaid,
    isInFreeTrial,
    isAdmin: accountData?.is_admin === true,
  })

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        accountId,
        accountData,
        isAnonymous,
        isKnown,
        isPaid,
        isInFreeTrial,
        canAccessExploreCoaching: exploreCoaching,
        isLoading,
        isAccountLoading,
        refreshAccountData,
        updateAccountField,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
